/* /industry — the company's industry pack: only the features, certificates, localisation quotas and shift/safety checks of the
 * business category chosen in Settings → Company (Tenant.category). Catalog: contracts/industries.json; maths: domain/industry.ts. */
import "./industry.css";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAllEmployees, useSettings, useSites, useToday, useUpdateEmployee, useUpdateSettings } from "../../data/hooks";
import type { BusinessCategory, Employee, EmpDocument, Settings } from "../../data/types";
import { isBusinessCategory } from "../../data/types";
import {
  certMatrix, heatCheck, HEAT_BAN, industryOf, INDUSTRIES, onSiteIds, quotaCheck, shiftApplied, siteCost,
  type CertCell, type CertState, type Industry, type IndustryCert, type IndustryFeature,
} from "../../domain/industry";
import { useI18n } from "../../i18n";
import { gDate } from "../../lib/format";
import {
  Alert, Bar, Button, Card, Chip, EmptyState, ErrorBox, Icon, Kpi, Modal, Money, PageHeader, SearchBox, Skeleton, Tabs, TextField, Toggle, cx, errMsg, useToast,
} from "../../ui";
import { useRole } from "../pay/components/common";
import { WEEKDAYS } from "../pay/Settings";
import { otPct } from "../../lib/country";

type Tab = "overview" | "certs" | "quota" | "shift";
const STATE_TONE: Record<CertState, string> = { valid: "ok", expiring: "orange", expired: "bad", missing: "bad", na: "" };

/* ---------- category picker (company without a category) ---------- */
function PickCategory({ canEdit }: { canEdit: boolean }) {
  const { L } = useI18n();
  const toast = useToast();
  const upd = useUpdateSettings();
  const choose = async (k: BusinessCategory) => {
    try {
      await upd.mutateAsync({ tenant: { category: k } });
      toast(L("Industry pack switched on", "تم تفعيل الحزمة القطاعية"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };
  return (
    <>
      <PageHeader title={L("Industry pack", "الحزمة القطاعية")} sub={L("Specialised features for your line of business — included in every plan.", "مزايا متخصصة لنشاطك — مشمولة في كل الباقات.")} />
      <Alert tone="info" tint testId="industry-none" title={L("Choose your business category to switch on its pack", "اختر نشاط منشأتك لتفعيل حزمته")}
        desc={canEdit ? L("You can change it later in Settings → Company.", "يمكنك تغييره لاحقًا من الإعدادات ← المنشأة.") : L("Ask the owner or HR to choose it in Settings → Company.", "اطلب من المالك أو الموارد البشرية اختياره من الإعدادات ← المنشأة.")} />
      <div className="ind-pick mt" data-testid="industry-picker">
        {INDUSTRIES.map((c) => (
          <button key={c.key} type="button" className="ind-pick-c" disabled={!canEdit || upd.isPending} onClick={() => { void choose(c.key); }} data-testid={"pick-" + c.key}>
            <span className="mod-ico"><Icon name={c.icon} /></span>
            <b>{L(...c.name)}</b>
            <span className="xs muted">{L(...c.tagline)}</span>
          </button>
        ))}
      </div>
    </>
  );
}

/* ---------- overview ---------- */
function FeatureCard({ f, onEngine }: { f: IndustryFeature; onEngine: (e: Tab | "sites") => void }) {
  const { L } = useI18n();
  const live = f.status === "live";
  const eng = f.engine;
  const open = !live || !eng ? null : eng.startsWith("/")
    ? <Link className="btn sm" to={eng} data-testid={"feat-open-" + f.key}><span>{L("Open", "فتح")}</span><Icon name="chevron-right" className="s" /></Link>
    : <Button variant="sm" end="chevron-right" onClick={() => onEngine(eng === "certs" ? "certs" : eng === "quota" ? "quota" : eng === "sites" ? "sites" : "shift")} data-testid={"feat-open-" + f.key}>{L("Open", "فتح")}</Button>;
  return (
    <div className={cx("ind-feat", !live && "planned")} data-testid={"feat-" + f.key}>
      <div className="row between nw">
        <b>{L(...f.title)}</b>
        {live ? <Chip tone="ok">{L("Live", "مفعّلة")}</Chip> : <Chip tone="">{L("On the roadmap", "ضمن خطة التطوير")}</Chip>}
      </div>
      <p className="sm muted">{L(...f.text)}</p>
      {open && <div className="row">{open}</div>}
    </div>
  );
}

function Overview({ ind, s, emps, onTab }: { ind: Industry; s: Settings; emps: Employee[]; onTab: (t: Tab | "sites") => void }) {
  const { L } = useI18n();
  const today = useToday();
  const sitesQ = useSites();
  const sites = sitesQ.data ?? [];
  const m = useMemo(() => certMatrix(ind, emps, sites, today), [ind, emps, sites, today]);
  const quotas = useMemo(() => ind.quotas.map((q) => quotaCheck(q, emps)), [ind, emps]);
  const live = ind.features.filter((f) => f.status === "live");
  const planned = ind.features.filter((f) => f.status === "planned");
  const active = sites.filter((x) => x.status === "Active");
  const cost = active.reduce((sum, x) => sum + siteCost(x, emps).total, 0);
  return (
    <div className="stack">
      <div className="g g-4">
        <Kpi testId="kpi-live" label={L("Live features", "مزايا مفعّلة")} value={String(live.length)} icon="zap" tone="gold" foot={L(`${planned.length} more on the roadmap`, `${planned.length} أخرى ضمن خطة التطوير`)} />
        <Kpi testId="kpi-cert-issues" label={L("Certificate issues", "ملاحظات الشهادات")} value={String(m.counts.expired + m.counts.missing)} icon="id-card" tone={m.counts.expired + m.counts.missing ? "bad" : undefined}
          foot={L(`${m.counts.expiring} expiring within 30 days`, `${m.counts.expiring} تنتهي خلال 30 يومًا`)} />
        <Kpi testId="kpi-quota-gaps" label={L("Localisation gaps", "فجوات التوطين")} value={String(quotas.filter((q) => !q.ok).length)} icon="target" tone={quotas.some((q) => !q.ok) ? "warn" : undefined}
          foot={L(`${quotas.reduce((n, q) => n + (q.applies ? q.need : 0), 0)} Saudi hires needed`, `${quotas.reduce((n, q) => n + (q.applies ? q.need : 0), 0)} تعيينًا سعوديًا مطلوبًا`)} />
        <Kpi testId="kpi-sites" label={L(...ind.sites.label)} value={String(active.length)} icon="map-pin" to="/sites" foot={<><Money value={cost} dec={0} /> {L("/ month", "/ شهريًا")}</>} />
      </div>
      <Card title={L("Specialised features", "المزايا المتخصصة")} sub={L(`Built for ${ind.name[0].toLowerCase()} companies in Saudi Arabia`, `مصممة لمنشآت ${ind.name[1]} في المملكة`)} testId="ind-features">
        <div className="ind-feats">{[...live, ...planned].map((f) => <FeatureCard key={f.key} f={f} onEngine={onTab} />)}</div>
      </Card>
      <div className="g g-2">
        <Card title={L("Typical pay elements", "عناصر الأجر المعتادة")} sub={L("Set them up as allowances, overtime or bonus lines in payroll.", "أعدّها كبدلات أو عمل إضافي أو مكافآت في الرواتب.")} testId="ind-pay">
          <div className="row">{ind.pay.map((p) => <span key={p[0]} className="chip pri">{L(...p)}</span>)}</div>
          <div className="row mt"><Link to="/payroll" className="btn sm ghost"><span>{L("Go to payroll", "الذهاب إلى الرواتب")}</span></Link></div>
        </Card>
        <Card title={L("Your company", "منشأتك")} testId="ind-company">
          <div className="stack sm">
            <div className="row between nw"><span className="muted">{L("Business category", "نشاط المنشأة")}</span><b>{L(...ind.name)}</b></div>
            <div className="row between nw"><span className="muted">{L("Employees covered", "الموظفون المشمولون")}</span><b className="num">{emps.filter((e) => e.status !== "Terminated").length}</b></div>
            <div className="row between nw"><span className="muted">{L("Shift policy", "سياسة الدوام")}</span><b className="num" dir="ltr">{s.attendance.shiftStart}–{s.attendance.shiftEnd}</b></div>
            <Link to="/settings" className="sm" data-testid="ind-change">{L("Change business category", "تغيير نشاط المنشأة")}</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}

/* ---------- certificates ---------- */
function RecordCertModal({ e, cert, cell, onClose }: { e: Employee; cert: IndustryCert; cell: CertCell; onClose: () => void }) {
  const { L, t, name } = useI18n();
  const today = useToday();
  const toast = useToast();
  const upd = useUpdateEmployee();
  const [num, setNum] = useState(cell.number ?? "");
  const [exp, setExp] = useState("");
  const [err, setErr] = useState<Record<string, string>>({});
  const save = async () => {
    const fe: Record<string, string> = {};
    if (!num.trim()) fe.number = L("Enter the certificate or licence number", "أدخل رقم الشهادة أو الترخيص");
    if (!/^\d{4}-\d{2}-\d{2}$/.test(exp)) fe.expiry = L("Enter the expiry date", "أدخل تاريخ الانتهاء");
    else if (exp <= today) fe.expiry = L("The expiry date must be after today", "يجب أن يكون تاريخ الانتهاء بعد اليوم");
    setErr(fe);
    if (Object.keys(fe).length) return;
    const doc: EmpDocument = { kind: cert.kind, number: num.trim(), expiry: exp };
    const documents = [...e.documents.filter((d) => d.kind !== cert.kind), doc];
    try {
      await upd.mutateAsync({ id: e.id, patch: { documents } });
      toast(L(`${cert.kind} recorded for ${e.nameEn}`, `تم تسجيل ${cert.ar} لـ ${e.nameAr}`));
      onClose();
    } catch (x) { toast(errMsg(x), "bad"); }
  };
  return (
    <Modal title={L(cert.kind, cert.ar)} onClose={onClose} testId="cert-modal"
      foot={<><Button onClick={onClose}>{t("Cancel")}</Button><Button variant="pri" onClick={() => { void save(); }} loading={upd.isPending} data-testid="cert-save">{t("Save")}</Button></>}>
      <div className="stack">
        <p className="sm muted" style={{ margin: 0 }}>{name(e)} · {e.code} · {e.title}{cert.note ? " — " + L(...cert.note) : ""}</p>
        <div className="form2">
          <TextField label={L("Certificate / licence number", "رقم الشهادة / الترخيص")} value={num} onChange={setNum} err={err.number} req dir="ltr" testId="cert-number" />
          <TextField label={L("Expiry date", "تاريخ الانتهاء")} type="date" value={exp} onChange={setExp} err={err.expiry} req testId="cert-expiry" />
        </div>
        <p className="xs muted" style={{ margin: 0 }}>{L("Saved to the employee's documents — you'll get expiry alerts in Documents & expiries.", "يُحفظ في وثائق الموظف — وستصلك تنبيهات الانتهاء في الوثائق وانتهاء الصلاحية.")}</p>
      </div>
    </Modal>
  );
}

function Certificates({ ind, emps }: { ind: Industry; emps: Employee[] }) {
  const { L, name, lang } = useI18n();
  const today = useToday();
  const { can } = useRole();
  const sitesQ = useSites();
  const sites = sitesQ.data ?? [];
  const m = useMemo(() => certMatrix(ind, emps, sites, today), [ind, emps, sites, today]);
  const [issues, setIssues] = useState(true);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState<{ e: Employee; i: number } | null>(null);
  const label = (st: CertState, c: CertCell) => st === "missing" ? L("Missing", "غير مسجلة") : st === "expired" ? L("Expired", "منتهية")
    : st === "expiring" ? L("Expires ", "تنتهي ") + gDate(c.expiry!, lang) : L("Valid to ", "سارية حتى ") + gDate(c.expiry!, lang);
  const rows = m.rows.filter((r) => (!issues || r.cells.some((c) => c.state === "missing" || c.state === "expired" || c.state === "expiring"))
    && (!q.trim() || (r.employee.nameEn + " " + r.employee.nameAr + " " + r.employee.code + " " + r.employee.title).toLowerCase().includes(q.trim().toLowerCase())));
  const siteCount = onSiteIds(sites).size;
  return (
    <div className="stack">
      <div className="g g-4">
        <Kpi testId="cert-valid" label={L("Valid", "سارية")} value={String(m.counts.valid)} icon="check" />
        <Kpi testId="cert-expiring" label={L("Expiring ≤ 30 days", "تنتهي خلال 30 يومًا")} value={String(m.counts.expiring)} icon="clock" tone={m.counts.expiring ? "warn" : undefined} />
        <Kpi testId="cert-expired" label={L("Expired", "منتهية")} value={String(m.counts.expired)} icon="alert" tone={m.counts.expired ? "bad" : undefined} />
        <Kpi testId="cert-missing" label={L("Missing", "غير مسجلة")} value={String(m.counts.missing)} icon="file" tone={m.counts.missing ? "bad" : undefined} />
      </div>
      <Card title={L("Required certificates & licences", "الشهادات والتراخيص المطلوبة")} testId="cert-rules">
        <ul className="ind-rules">
          {ind.certs.map((c) => (
            <li key={c.kind}><b>{L(c.kind, c.ar)}</b>
              <span className="xs muted">{c.scope === "site" ? L("Staff on an active site", "الموظفون في موقع نشط") : L("All staff", "كل الموظفين")}{c.match ? L(" · titles containing: ", " · المسميات التي تحتوي: ") + c.match.map((k) => k.trim()).join(", ") : ""}{c.note ? " — " + L(...c.note) : ""}</span></li>
          ))}
        </ul>
        {ind.certs.some((c) => c.scope === "site") && siteCount === 0 && (
          <Alert tone="info" tint title={L("Site certificates apply once people are allocated to a site", "تُطبق شهادات المواقع بعد تخصيص الموظفين لموقع")} action={<Link className="btn sm" to="/sites"><span>{L(...ind.sites.label)}</span></Link>} />
        )}
      </Card>
      <section className="card">
        <div className="toolbar">
          <SearchBox value={q} onChange={setQ} placeholder={L("Search employee or title…", "ابحث بالموظف أو المسمى…")} testId="cert-search" />
          <Toggle checked={issues} onChange={setIssues} label={L("Only show issues", "عرض الملاحظات فقط")} testId="cert-issues" />
        </div>
        {rows.length === 0 ? (
          <EmptyState title={issues ? L("No certificate issues", "لا توجد ملاحظات على الشهادات") : L("No employees need these certificates", "لا يوجد موظفون يحتاجون هذه الشهادات")}
            desc={issues ? L("Everyone who needs a certificate has a valid one.", "كل من يحتاج شهادة لديه شهادة سارية.") : L("Check job titles and site allocation.", "تحقق من المسميات وتخصيص المواقع.")} />
        ) : (
          <ul className="ind-matrix" data-testid="cert-matrix">
            {rows.map((r) => (
              <li key={r.employee.id} data-testid={"cert-row-" + r.employee.id}>
                <div className="ind-who"><b>{name(r.employee)}</b><span className="xs muted">{r.employee.code} · {r.employee.title}</span></div>
                <div className="ind-cells">
                  {r.cells.map((c, i) => c.state === "na" ? null : (
                    <button key={i} type="button" className={cx("chip dot ind-cell", STATE_TONE[c.state])} disabled={!can("hr.modules")}
                      title={L(ind.certs[i].kind, ind.certs[i].ar)} onClick={() => setEdit({ e: r.employee, i })} data-testid={`cert-cell-${r.employee.id}-${i}`}>
                      <span className="ind-cell-k">{L(ind.certs[i].kind, ind.certs[i].ar)}</span><span>{label(c.state, c)}</span>
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      {edit && <RecordCertModal e={edit.e} cert={ind.certs[edit.i]} cell={m.rows.find((r) => r.employee.id === edit.e.id)!.cells[edit.i]} onClose={() => setEdit(null)} />}
    </div>
  );
}

/* ---------- localisation quotas ---------- */
function Quotas({ ind, emps }: { ind: Industry; emps: Employee[] }) {
  const { L } = useI18n();
  const rs = useMemo(() => ind.quotas.map((q) => quotaCheck(q, emps)), [ind, emps]);
  return (
    <div className="stack">
      <Alert tone="info" tint title={L("How this is counted", "طريقة الاحتساب")}
        desc={L("Staff are grouped by keywords in their job title. A Saudi counts when paid at least the rule's minimum wage (full wage). Rules apply from the minimum staff count. Confirm occupation codes on Qiwa — they decide the official result.", "يُجمع الموظفون حسب كلمات في المسمى الوظيفي. يُحتسب السعودي إذا بلغ أجره الكامل الحد الأدنى للقاعدة. تُطبق القواعد ابتداءً من الحد الأدنى للعدد. تحقق من رموز المهن في قوى — فهي التي تحدد النتيجة الرسمية.")} />
      <div className="g g-2">
        {rs.map((r) => (
          <Card key={r.rule.group[0]} title={L(...r.rule.group)} testId={"quota-" + r.rule.match[0].trim().replace(/\W+/g, "-")}
            actions={!r.applies ? <Chip tone="">{L("Not applicable yet", "لا تنطبق بعد")}</Chip> : r.ok ? <Chip tone="ok">{L("Meets the rule", "مستوفاة")}</Chip> : <Chip tone="bad">{L(`${r.need} more needed`, `مطلوب ${r.need} إضافي`)}</Chip>}>
            <div className="stack sm">
              <div className="row between nw"><span className="muted">{L("Required Saudi share", "نسبة التوطين المطلوبة")}</span><b className="num">{r.rule.pct}%</b></div>
              <div className="row between nw"><span className="muted">{L("Your share", "نسبتك")}</span><b className="num" data-testid="quota-pct">{r.pct}%</b></div>
              <Bar value={r.pct} max={100} tone={r.ok ? "ok" : "bad"} />
              <div className="row between nw xs muted">
                <span>{L(`${r.staff} staff in group · ${r.saudis} Saudi · ${r.counted} counted`, `${r.staff} في المجموعة · ${r.saudis} سعودي · ${r.counted} محتسب`)}</span>
                <span>{L(`needs ${r.required}`, `المطلوب ${r.required}`)}</span>
              </div>
              <span className="xs muted">{L(`Applies from ${r.rule.minStaff} staff`, `تنطبق من ${r.rule.minStaff} موظفين`)}{r.rule.minWage ? L(` · min. wage SAR ${r.rule.minWage.toLocaleString("en")}`, ` · الحد الأدنى للأجر ${r.rule.minWage.toLocaleString("en")} ر.س`) : ""}{r.rule.note ? " — " + L(...r.rule.note) : ""}</span>
            </div>
          </Card>
        ))}
      </div>
      <div className="row"><Link className="btn ghost sm" to="/saudization"><span>{L("Open Nitaqat & Saudization", "فتح نطاقات والتوطين")}</span></Link><Link className="btn ghost sm" to="/recruit"><span>{L("Plan Saudi hires", "خطط لتعيين سعوديين")}</span></Link></div>
    </div>
  );
}

/* ---------- shifts & safety ---------- */
function ShiftSafety({ ind, s }: { ind: Industry; s: Settings }) {
  const { L, lang } = useI18n();
  const today = useToday();
  const toast = useToast();
  const { can } = useRole();
  const upd = useUpdateSettings();
  const sitesQ = useSites();
  const applied = shiftApplied(ind, s.attendance);
  const h = heatCheck(today, s.attendance);
  const outdoor = (sitesQ.data ?? []).filter((x) => x.status === "Active" && x.outdoor);
  const outdoorStaff = new Set(outdoor.flatMap((x) => x.employeeIds)).size;
  const days = (d: number[]) => d.length ? d.map((i) => L(...WEEKDAYS[i])).join(L(", ", "، ")) : L("None (7-day operation)", "لا يوجد (تشغيل 7 أيام)");
  const apply = async () => {
    try {
      await upd.mutateAsync({ attendance: { ...s.attendance, shiftStart: ind.shift.start, shiftEnd: ind.shift.end, weekendDays: ind.shift.weekend } });
      toast(L("Recommended shift applied to your attendance policy", "تم تطبيق الدوام الموصى به على سياسة الحضور"));
    } catch (e) { toast(errMsg(e), "bad"); }
  };
  return (
    <div className="g g-2">
      <Card title={L("Recommended shift", "الدوام الموصى به")} sub={L(...ind.shift.note)} testId="ind-shift"
        actions={applied ? <Chip tone="ok">{L("In use", "مطبّق")}</Chip> : undefined}>
        <div className="stack sm">
          <div className="row between nw"><span className="muted">{L("Recommended", "الموصى به")}</span><b className="num" dir="ltr">{ind.shift.start}–{ind.shift.end}</b></div>
          <div className="row between nw"><span className="muted">{L("Weekly rest days", "أيام الراحة")}</span><b>{days(ind.shift.weekend)}</b></div>
          <div className="row between nw"><span className="muted">{L("Your policy", "سياستك")}</span><b className="num" dir="ltr" data-testid="ind-shift-current">{s.attendance.shiftStart}–{s.attendance.shiftEnd}</b></div>
          <div className="row between nw"><span className="muted">{L("Your rest days", "أيام راحتك")}</span><b>{days(s.attendance.weekendDays)}</b></div>
          {!applied && (can("settings.write")
            ? <div className="row"><Button variant="pri" icon="check" loading={upd.isPending} onClick={() => { void apply(); }} data-testid="ind-shift-apply">{L("Apply to attendance policy", "تطبيق على سياسة الحضور")}</Button><Link className="btn ghost" to="/settings"><span>{L("Fine-tune in Settings", "تعديل من الإعدادات")}</span></Link></div>
            : <span className="xs muted">{L("The owner or HR can apply it.", "يمكن للمالك أو الموارد البشرية تطبيقه.")}</span>)}
          <span className="xs muted">{L("Weekly hours must stay within 48 (36 in Ramadan for Muslim staff); overtime is paid at " + otPct() + " of the basic hourly wage.", "يجب ألا تتجاوز ساعات العمل 48 أسبوعيًا (36 في رمضان للمسلمين)، ويُدفع العمل الإضافي بنسبة " + otPct() + " من الأجر الأساسي للساعة.")}</span>
        </div>
      </Card>
      {ind.heat ? (
        <Card title={L("Summer midday work ban", "حظر العمل وقت الظهيرة صيفًا")} sub={L(`No outdoor work in direct sun ${HEAT_BAN.start}–${HEAT_BAN.end}, 15 June – 15 September.`, `يُمنع العمل تحت أشعة الشمس في الأماكن المكشوفة ${HEAT_BAN.start}–${HEAT_BAN.end} من 15 يونيو حتى 15 سبتمبر.`)} testId="ind-heat"
          actions={h.inSeason ? <Chip tone="bad">{L("Ban in force", "الحظر ساري")}</Chip> : <Chip tone="info">{L(`Starts in ${h.daysToSeason} days`, `يبدأ بعد ${h.daysToSeason} يومًا`)}</Chip>}>
          <div className="stack sm">
            <div className="row between nw"><span className="muted">{L("Season", "الموسم")}</span><b>{gDate(h.seasonStart, lang)} – {gDate(h.seasonEnd, lang)}</b></div>
            <div className="row between nw"><span className="muted">{L("Overlap with your shift", "التداخل مع دوامك")}</span><b className="num" data-testid="heat-overlap">{L(`${h.overlapMinutes} min`, `${h.overlapMinutes} دقيقة`)}</b></div>
            <div className="row between nw"><span className="muted">{L("Active outdoor sites", "المواقع المكشوفة النشطة")}</span><b className="num" data-testid="heat-sites">{outdoor.length}</b></div>
            <div className="row between nw"><span className="muted">{L("Workers on outdoor sites", "العمال في مواقع مكشوفة")}</span><b className="num" data-testid="heat-staff">{outdoorStaff}</b></div>
            {h.shiftOverlaps && outdoorStaff > 0
              ? <Alert tone={h.inSeason ? "bad" : "warn"} testId="heat-alert" title={h.inSeason ? L("Outdoor crews are rostered during the ban", "طواقم المواقع المكشوفة مجدولة وقت الحظر") : L("Plan a split shift before the season starts", "خطط لدوام مقسم قبل بدء الموسم")}
                desc={L("Move outdoor work out of 12:00–15:00 (for example 06:00–12:00 and 15:00–18:00) or move crews indoors. Violations are fined per worker.", "انقل العمل المكشوف خارج 12:00–15:00 (مثل 06:00–12:00 و15:00–18:00) أو انقل الطواقم إلى أماكن مظللة. تُفرض الغرامات لكل عامل.")} />
              : <Alert tone="ok" tint testId="heat-ok" title={outdoorStaff ? L("Your shift avoids the ban window", "دوامك يتجنب فترة الحظر") : L("No workers on outdoor sites", "لا يوجد عمال في مواقع مكشوفة")} desc={L("Mark sites as outdoor on the sites page so they are checked here.", "حدّد المواقع المكشوفة في صفحة المواقع ليتم فحصها هنا.")} />}
            <Link className="sm" to="/sites">{L(...ind.sites.label)}</Link>
          </div>
        </Card>
      ) : (
        <Card title={L("Working-time rules", "قواعد وقت العمل")} testId="ind-hours">
          <ul className="ind-rules">
            <li><b>{L("8 hours a day, 48 a week", "8 ساعات يوميًا و48 أسبوعيًا")}</b><span className="xs muted">{L("6 a day / 36 a week in Ramadan for Muslim staff.", "6 ساعات يوميًا و36 أسبوعيًا في رمضان للمسلمين.")}</span></li>
            <li><b>{L("Rest break", "فترة الراحة")}</b><span className="xs muted">{L("At least 30 minutes after 5 consecutive hours.", "30 دقيقة على الأقل بعد 5 ساعات متواصلة.")}</span></li>
            <li><b>{L("Weekly rest day", "يوم الراحة الأسبوعي")}</b><span className="xs muted">{L("At least one full paid day a week; Friday by default.", "يوم كامل مدفوع أسبوعيًا على الأقل؛ الجمعة افتراضيًا.")}</span></li>
          </ul>
        </Card>
      )}
    </div>
  );
}

/* ---------- page ---------- */
export default function IndustryPage() {
  const { L } = useI18n();
  const { can } = useRole();
  const sQ = useSettings();
  const eQ = useAllEmployees();
  const [tab, setTab] = useState<Tab>("overview");
  const nav = useNavigate();
  if (sQ.error) return <ErrorBox error={sQ.error} onRetry={() => sQ.refetch()} />;
  if (eQ.error) return <ErrorBox error={eQ.error} onRetry={() => eQ.refetch()} />;
  if (!sQ.data || !eQ.data) return <Skeleton />;
  const s = sQ.data;
  const cat = s.tenant.category;
  const ind = isBusinessCategory(cat) ? industryOf(cat) : undefined;
  if (!ind) return <PickCategory canEdit={can("settings.write")} />;
  const emps = eQ.data;
  const go = (t: Tab | "sites") => { if (t === "sites") nav("/sites"); else setTab(t); };
  return (
    <div data-testid="industry">
      <PageHeader title={<span className="row nw"><span className="kpi-ico gold"><Icon name={ind.icon} /></span>{L(`${ind.name[0]} pack`, `حزمة ${ind.name[1]}`)}</span>} sub={L(...ind.tagline)}
        actions={<Link className="btn ghost" to="/settings" data-testid="ind-settings"><Icon name="settings" className="s" /><span>{L("Change category", "تغيير النشاط")}</span></Link>} />
      <Tabs label={L("Industry pack", "الحزمة القطاعية")} value={tab} onChange={setTab}
        items={[["overview", L("Overview", "نظرة عامة")], ["certs", L("Certificates", "الشهادات")], ["quota", L("Localisation", "التوطين")], ["shift", L("Shifts & safety", "الدوام والسلامة")]]} />
      <div className="mt">
        {tab === "overview" && <Overview ind={ind} s={s} emps={emps} onTab={go} />}
        {tab === "certs" && <Certificates ind={ind} emps={emps} />}
        {tab === "quota" && <Quotas ind={ind} emps={emps} />}
        {tab === "shift" && <ShiftSafety ind={ind} s={s} />}
      </div>
    </div>
  );
}
